import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Description as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  VideoFile as VideoIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import projectService, { DOCUMENT_TYPES } from '../../services/projectService';

interface DocumentUploadProps {
  projectCode: string;
  projectContextId?: number;
  currentUser: string;
  onUploadSuccess: () => void;
}

interface UploadFile {
  file: File;
  id: string;
  progress: number;
  error?: string;
  uploaded?: boolean;
}

interface DocumentFormData {
  document_type: string;
  description: string;
  is_active: boolean;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  projectCode,
  projectContextId,
  currentUser,
  onUploadSuccess
}) => {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<UploadFile | null>(null);
  const [documentTypes, setDocumentTypes] = useState<string[]>(Object.values(DOCUMENT_TYPES));

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<DocumentFormData>({
    defaultValues: {
      document_type: '',
      description: '',
      is_active: true
    }
  });

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = (newFiles: File[]) => {
    const uploadFiles: UploadFile[] = newFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      progress: 0
    }));

    setFiles(prev => [...prev, ...uploadFiles]);
  };

  const removeFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) {
      return <ImageIcon color="primary" />;
    } else if (file.type === 'application/pdf') {
      return <PdfIcon color="error" />;
    } else if (file.type.startsWith('video/')) {
      return <VideoIcon color="secondary" />;
    }
    return <FileIcon color="action" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getDocumentTypeLabel = (documentType: string) => {
    const labels: { [key: string]: string } = {
      brochure: 'Brochure',
      floor_plan: 'Plano',
      additional_info: 'Información Adicional',
      image: 'Imagen',
      video: 'Video',
      document: 'Documento'
    };
    return labels[documentType] || documentType;
  };

  const openUploadForm = (file: UploadFile) => {
    setSelectedFile(file);
    
    // Auto-detect document type based on file type
    let autoType = 'document';
    if (file.file.type.startsWith('image/')) {
      autoType = 'image';
    } else if (file.file.type.startsWith('video/')) {
      autoType = 'video';
    } else if (file.file.type === 'application/pdf') {
      autoType = 'additional_info';
    }

    reset({
      document_type: autoType,
      description: '',
      is_active: true
    });
    
    setFormDialogOpen(true);
  };

  const onSubmitUpload = async (data: DocumentFormData) => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      // Update file progress
      setFiles(prev => prev.map(f => 
        f.id === selectedFile.id 
          ? { ...f, progress: 50 }
          : f
      ));

      await projectService.uploadProjectDocument(
        selectedFile.file,
        projectCode,
        data.document_type,
        currentUser,
        projectContextId,
        data.description || undefined,
        data.is_active
      );

      // Mark as uploaded
      setFiles(prev => prev.map(f => 
        f.id === selectedFile.id 
          ? { ...f, progress: 100, uploaded: true }
          : f
      ));

      setFormDialogOpen(false);
      setSelectedFile(null);
      onUploadSuccess();

      // Remove uploaded file after a delay
      setTimeout(() => {
        removeFile(selectedFile.id);
      }, 2000);

    } catch (err: any) {
      // Mark as error
      setFiles(prev => prev.map(f => 
        f.id === selectedFile.id 
          ? { ...f, error: err.response?.data?.detail || 'Error al subir archivo' }
          : f
      ));
    } finally {
      setUploading(false);
    }
  };

  const handleFormClose = () => {
    if (!uploading) {
      setFormDialogOpen(false);
      setSelectedFile(null);
    }
  };

  return (
    <Box>
      {/* Drag and Drop Area */}
      <Paper
        sx={{
          p: 4,
          border: '2px dashed',
          borderColor: dragActive ? 'primary.main' : 'grey.300',
          bgcolor: dragActive ? 'primary.50' : 'grey.50',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'primary.50'
          }
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => document.getElementById('file-input')?.click()}
      >
        <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Arrastra archivos aquí o haz clic para seleccionar
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Soporta imágenes, PDFs, videos y documentos
        </Typography>
        <input
          id="file-input"
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileInput}
          accept="image/*,application/pdf,video/*,.doc,.docx,.xls,.xlsx,.txt"
        />
      </Paper>

      {/* File List */}
      {files.length > 0 && (
        <Box mt={3}>
          <Typography variant="h6" gutterBottom>
            Archivos seleccionados ({files.length})
          </Typography>
          
          {files.map((uploadFile) => (
            <Paper key={uploadFile.id} sx={{ p: 2, mb: 1 }}>
              <Box display="flex" alignItems="center" gap={2}>
                {getFileIcon(uploadFile.file)}
                
                <Box flex={1}>
                  <Typography variant="body2" fontWeight="medium">
                    {uploadFile.file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(uploadFile.file.size)}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  {uploadFile.uploaded && (
                    <Chip label="Subido" color="success" size="small" />
                  )}
                  {uploadFile.error && (
                    <Chip label="Error" color="error" size="small" />
                  )}
                  {uploadFile.progress > 0 && uploadFile.progress < 100 && (
                    <Box width={100}>
                      <LinearProgress 
                        variant="determinate" 
                        value={uploadFile.progress} 
                      />
                    </Box>
                  )}
                  
                  {!uploadFile.uploaded && !uploadFile.error && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openUploadForm(uploadFile)}
                      disabled={uploading}
                    >
                      Subir
                    </Button>
                  )}
                  
                  <IconButton
                    size="small"
                    onClick={() => removeFile(uploadFile.id)}
                    disabled={uploading && selectedFile?.id === uploadFile.id}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>

              {uploadFile.error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {uploadFile.error}
                </Alert>
              )}
            </Paper>
          ))}
        </Box>
      )}

      {/* Upload Form Dialog */}
      <Dialog open={formDialogOpen} onClose={handleFormClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Configurar documento
        </DialogTitle>
        
        <form onSubmit={handleSubmit(onSubmitUpload)}>
          <DialogContent>
            {selectedFile && (
              <Box mb={3} p={2} bgcolor="grey.50" borderRadius={1}>
                <Box display="flex" alignItems="center" gap={2}>
                  {getFileIcon(selectedFile.file)}
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {selectedFile.file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(selectedFile.file.size)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            <Box display="flex" flexDirection="column" gap={3}>
              <Controller
                name="document_type"
                control={control}
                rules={{ required: 'El tipo de documento es requerido' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.document_type}>
                    <InputLabel>Tipo de Documento</InputLabel>
                    <Select {...field} label="Tipo de Documento">
                      {documentTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {getDocumentTypeLabel(type)}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.document_type && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                        {errors.document_type.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />

              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Descripción (Opcional)"
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Descripción del documento..."
                  />
                )}
              />

              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={field.onChange}
                        color="primary"
                      />
                    }
                    label="Documento activo"
                  />
                )}
              />
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleFormClose} disabled={uploading}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={uploading}
              startIcon={uploading ? <LinearProgress /> : <UploadIcon />}
            >
              {uploading ? 'Subiendo...' : 'Subir Documento'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default DocumentUpload;